import { useCallback, useMemo, useReducer } from "react";
import type { Dispatch, SetStateAction } from "react";
import type {
  AddEntryResult,
  AccommodationEntry,
  BusinessTravelEntry,
  EmployeeCommutingEntry,
  FreightEntry,
  Scope3CoverageData,
  Scope3Data,
  SpendBasedEntry,
} from "./types";
import {
  calculateBusinessTravelEmissions,
  calculateAccommodationEmissions,
  calculateEmployeeCommutingEmissions,
  calculateSpendBasedEmissions,
  calculateFreightEmissions,
} from "@/service/emissions";

export const DEFAULT_BUSINESS_TRAVEL_ENTRY: Partial<BusinessTravelEntry> = {
  mode: "flight_domestic",
  period: null,
};

export const DEFAULT_EMPLOYEE_COMMUTING_ENTRY: Partial<EmployeeCommutingEntry> = {
  mode: "car_petrol_avg",
  distancePerDayKm: 0,
  daysPerWeek: 5,
  weeksPerYear: 48,
  employeeCount: 1,
  passengerKm: 0,
  period: null,
};

export const DEFAULT_FREIGHT_ENTRY: Partial<FreightEntry> = {
  direction: "upstream",
  mode: "road_freight_hgv",
  unit: "tonne_km",
  period: null,
};

export const DEFAULT_SPEND_BASED_ENTRY: Partial<SpendBasedEntry> = {
  category: "spend_based",
  subCategory: "office_supplies",
  currency: "GBP",
  period: null,
};

type Scope3State = {
  scope3Data: Scope3Data;
  currentBusinessTravelEntry: Partial<BusinessTravelEntry>;
  currentAccommodationEntry: Partial<AccommodationEntry>;
  currentEmployeeCommutingEntry: Partial<EmployeeCommutingEntry>;
  currentSpendBasedEntry: Partial<SpendBasedEntry>;
  currentFreightEntry: Partial<FreightEntry>;
};

type Scope3Action =
  | { type: "setScope3Data"; payload: SetStateAction<Scope3Data> }
  | { type: "updateScope3Data"; payload: Partial<Scope3Data> }
  | { type: "updateScope3Coverage"; payload: Partial<Scope3CoverageData> }
  | { type: "setCurrentBusinessTravelEntry"; payload: SetStateAction<Partial<BusinessTravelEntry>> }
  | { type: "setCurrentAccommodationEntry"; payload: SetStateAction<Partial<AccommodationEntry>> }
  | { type: "setCurrentEmployeeCommutingEntry"; payload: SetStateAction<Partial<EmployeeCommutingEntry>> }
  | { type: "setCurrentSpendBasedEntry"; payload: SetStateAction<Partial<SpendBasedEntry>> }
  | { type: "setCurrentFreightEntry"; payload: SetStateAction<Partial<FreightEntry>> }
  | { type: "addBusinessTravelEntry"; payload: BusinessTravelEntry }
  | { type: "updateBusinessTravelEntry"; payload: { id: string; updates: Partial<BusinessTravelEntry> } }
  | { type: "deleteBusinessTravelEntry"; payload: string }
  | { type: "addAccommodationEntry"; payload: AccommodationEntry }
  | { type: "updateAccommodationEntry"; payload: { id: string; updates: Partial<AccommodationEntry> } }
  | { type: "deleteAccommodationEntry"; payload: string }
  | { type: "addEmployeeCommutingEntry"; payload: EmployeeCommutingEntry }
  | { type: "updateEmployeeCommutingEntry"; payload: { id: string; updates: Partial<EmployeeCommutingEntry> } }
  | { type: "deleteEmployeeCommutingEntry"; payload: string }
  | { type: "addSpendBasedEntry"; payload: SpendBasedEntry }
  | { type: "updateSpendBasedEntry"; payload: { id: string; updates: Partial<SpendBasedEntry> } }
  | { type: "deleteSpendBasedEntry"; payload: string }
  | { type: "addFreightEntry"; payload: FreightEntry }
  | { type: "updateFreightEntry"; payload: { id: string; updates: Partial<FreightEntry> } }
  | { type: "deleteFreightEntry"; payload: string };

const initialScope3State: Scope3State = {
  scope3Data: {
    businessTravelEntries: [],
    accommodationEntries: [],
    employeeCommutingEntries: [],
    spendBasedEntries: [],
    freightEntries: [],
    coverageData: {
      level: null,
      justification: "",
    },
    supportingDocuments: [],
  },
  currentBusinessTravelEntry: {
    mode: "flight_domestic",
    period: null,
  },
  currentAccommodationEntry: {
    period: null,
  },
  currentEmployeeCommutingEntry: {
    mode: "car_petrol_avg",
    distancePerDayKm: 0,
    daysPerWeek: 5,
    weeksPerYear: 48,
    employeeCount: 1,
    passengerKm: 0,
    period: null,
  },
  currentSpendBasedEntry: {
    category: "spend_based",
    subCategory: "office_supplies",
    currency: "GBP",
    period: null,
  },
  currentFreightEntry: {
    direction: "upstream",
    mode: "road_freight_hgv",
    unit: "tonne_km",
    period: null,
  },
};

function resolveSetState<T>(value: SetStateAction<T>, current: T): T {
  return typeof value === "function" ? (value as (prev: T) => T)(current) : value;
}

function scope3Reducer(state: Scope3State, action: Scope3Action): Scope3State {
  switch (action.type) {
    case "setScope3Data":
      return { ...state, scope3Data: resolveSetState(action.payload, state.scope3Data) };
    case "updateScope3Data":
      return { ...state, scope3Data: { ...state.scope3Data, ...action.payload } };
    case "updateScope3Coverage":
      return {
        ...state,
        scope3Data: {
          ...state.scope3Data,
          coverageData: { ...state.scope3Data.coverageData, ...action.payload },
        },
      };
    case "setCurrentBusinessTravelEntry":
      return {
        ...state,
        currentBusinessTravelEntry: resolveSetState(action.payload, state.currentBusinessTravelEntry),
      };
    case "setCurrentAccommodationEntry":
      return {
        ...state,
        currentAccommodationEntry: resolveSetState(action.payload, state.currentAccommodationEntry),
      };
    case "setCurrentEmployeeCommutingEntry":
      return {
        ...state,
        currentEmployeeCommutingEntry: resolveSetState(action.payload, state.currentEmployeeCommutingEntry),
      };
    case "setCurrentSpendBasedEntry":
      return {
        ...state,
        currentSpendBasedEntry: resolveSetState(action.payload, state.currentSpendBasedEntry),
      };
    case "setCurrentFreightEntry":
      return {
        ...state,
        currentFreightEntry: resolveSetState(action.payload, state.currentFreightEntry),
      };
    case "addBusinessTravelEntry":
      return {
        ...state,
        scope3Data: {
          ...state.scope3Data,
          businessTravelEntries: [...state.scope3Data.businessTravelEntries, action.payload],
        },
      };
    case "updateBusinessTravelEntry":
      return {
        ...state,
        scope3Data: {
          ...state.scope3Data,
          businessTravelEntries: state.scope3Data.businessTravelEntries.map((entry) =>
            entry.id === action.payload.id ? { ...entry, ...action.payload.updates } : entry
          ),
        },
      };
    case "deleteBusinessTravelEntry":
      return {
        ...state,
        scope3Data: {
          ...state.scope3Data,
          businessTravelEntries: state.scope3Data.businessTravelEntries.filter(
            (entry) => entry.id !== action.payload
          ),
        },
      };
    case "addAccommodationEntry":
      return {
        ...state,
        scope3Data: {
          ...state.scope3Data,
          accommodationEntries: [...state.scope3Data.accommodationEntries, action.payload],
        },
      };
    case "updateAccommodationEntry":
      return {
        ...state,
        scope3Data: {
          ...state.scope3Data,
          accommodationEntries: state.scope3Data.accommodationEntries.map((entry) =>
            entry.id === action.payload.id ? { ...entry, ...action.payload.updates } : entry
          ),
        },
      };
    case "deleteAccommodationEntry":
      return {
        ...state,
        scope3Data: {
          ...state.scope3Data,
          accommodationEntries: state.scope3Data.accommodationEntries.filter(
            (entry) => entry.id !== action.payload
          ),
        },
      };
    case "addEmployeeCommutingEntry":
      return {
        ...state,
        scope3Data: {
          ...state.scope3Data,
          employeeCommutingEntries: [...state.scope3Data.employeeCommutingEntries, action.payload],
        },
      };
    case "updateEmployeeCommutingEntry":
      return {
        ...state,
        scope3Data: {
          ...state.scope3Data,
          employeeCommutingEntries: state.scope3Data.employeeCommutingEntries.map((entry) =>
            entry.id === action.payload.id ? { ...entry, ...action.payload.updates } : entry
          ),
        },
      };
    case "deleteEmployeeCommutingEntry":
      return {
        ...state,
        scope3Data: {
          ...state.scope3Data,
          employeeCommutingEntries: state.scope3Data.employeeCommutingEntries.filter(
            (entry) => entry.id !== action.payload
          ),
        },
      };
    case "addSpendBasedEntry":
      return {
        ...state,
        scope3Data: {
          ...state.scope3Data,
          spendBasedEntries: [...state.scope3Data.spendBasedEntries, action.payload],
        },
      };
    case "updateSpendBasedEntry":
      return {
        ...state,
        scope3Data: {
          ...state.scope3Data,
          spendBasedEntries: state.scope3Data.spendBasedEntries.map((entry) =>
            entry.id === action.payload.id ? { ...entry, ...action.payload.updates } : entry
          ),
        },
      };
    case "deleteSpendBasedEntry":
      return {
        ...state,
        scope3Data: {
          ...state.scope3Data,
          spendBasedEntries: state.scope3Data.spendBasedEntries.filter((entry) => entry.id !== action.payload),
        },
      };
    case "addFreightEntry":
      return {
        ...state,
        scope3Data: {
          ...state.scope3Data,
          freightEntries: [...state.scope3Data.freightEntries, action.payload],
        },
      };
    case "updateFreightEntry":
      return {
        ...state,
        scope3Data: {
          ...state.scope3Data,
          freightEntries: state.scope3Data.freightEntries.map((entry) =>
            entry.id === action.payload.id ? { ...entry, ...action.payload.updates } : entry
          ),
        },
      };
    case "deleteFreightEntry":
      return {
        ...state,
        scope3Data: {
          ...state.scope3Data,
          freightEntries: state.scope3Data.freightEntries.filter((entry) => entry.id !== action.payload),
        },
      };
    default:
      return state;
  }
}

type Scope3Actions = {
  setScope3Data: Dispatch<SetStateAction<Scope3Data>>;
  updateScope3Data: (updates: Partial<Scope3Data>) => void;
  updateScope3Coverage: (updates: Partial<Scope3CoverageData>) => void;
  setCurrentBusinessTravelEntry: Dispatch<SetStateAction<Partial<BusinessTravelEntry>>>;
  setCurrentAccommodationEntry: Dispatch<SetStateAction<Partial<AccommodationEntry>>>;
  setCurrentEmployeeCommutingEntry: Dispatch<SetStateAction<Partial<EmployeeCommutingEntry>>>;
  setCurrentSpendBasedEntry: Dispatch<SetStateAction<Partial<SpendBasedEntry>>>;
  setCurrentFreightEntry: Dispatch<SetStateAction<Partial<FreightEntry>>>;
  addBusinessTravelEntry: () => Promise<AddEntryResult>;
  addAccommodationEntry: () => Promise<AddEntryResult>;
  addEmployeeCommutingEntry: () => Promise<AddEntryResult>;
  addSpendBasedEntry: () => Promise<AddEntryResult>;
  addFreightEntry: () => Promise<AddEntryResult>;
  updateBusinessTravelEntry: (id: string, updates: Partial<BusinessTravelEntry>) => void;
  updateEmployeeCommutingEntry: (id: string, updates: Partial<EmployeeCommutingEntry>) => void;
  updateFreightEntry: (id: string, updates: Partial<FreightEntry>) => void;
  updateSpendBasedEntry: (id: string, updates: Partial<SpendBasedEntry>) => void;
  deleteBusinessTravelEntry: (id: string) => void;
  deleteAccommodationEntry: (id: string) => void;
  deleteEmployeeCommutingEntry: (id: string) => void;
  deleteSpendBasedEntry: (id: string) => void;
  deleteFreightEntry: (id: string) => void;
};

export function useScope3State(): { state: Scope3State; actions: Scope3Actions } {
  const [state, dispatch] = useReducer(scope3Reducer, initialScope3State);

  const addBusinessTravelEntry = useCallback(async (): Promise<AddEntryResult> => {
    const distanceKm = state.currentBusinessTravelEntry.distanceKm;
    const period = state.currentBusinessTravelEntry.period;

    if (!state.currentBusinessTravelEntry.mode) {
      return { ok: false, error: "Please select a travel mode" };
    }
    if (distanceKm === undefined || distanceKm === null || distanceKm <= 0) {
      return { ok: false, error: "Please enter a distance greater than zero" };
    }
    if (!period) {
      return { ok: false, error: "Please select a period" };
    }

    const newEntry: BusinessTravelEntry = {
      id: `businesstravel-${Date.now()}`,
      mode: state.currentBusinessTravelEntry.mode,
      distanceKm,
      period,
      description: state.currentBusinessTravelEntry.description,
      tco2e: null,
      calculating: true,
    };

    dispatch({ type: "addBusinessTravelEntry", payload: newEntry });

    const tco2e = await calculateBusinessTravelEmissions(newEntry);
    dispatch({
      type: "updateBusinessTravelEntry",
      payload: { id: newEntry.id, updates: { tco2e, calculating: false } },
    });

    dispatch({
      type: "setCurrentBusinessTravelEntry",
      payload: { mode: "flight_domestic", period: null },
    });

    return { ok: true, tco2e };
  }, [state.currentBusinessTravelEntry]);

  const addAccommodationEntry = useCallback(async (): Promise<AddEntryResult> => {
    const hotelNights = state.currentAccommodationEntry.hotelNights;
    const period = state.currentAccommodationEntry.period;

    if (hotelNights === undefined || hotelNights === null || hotelNights <= 0) {
      return { ok: false, error: "Please enter hotel nights greater than zero" };
    }
    if (!period) {
      return { ok: false, error: "Please select a period" };
    }

    const newEntry: AccommodationEntry = {
      id: `accommodation-${Date.now()}`,
      hotelNights,
      country: state.currentAccommodationEntry.country,
      countryCode: state.currentAccommodationEntry.countryCode,
      period,
      tco2e: null,
      calculating: true,
    };

    dispatch({ type: "addAccommodationEntry", payload: newEntry });

    const tco2e = await calculateAccommodationEmissions(newEntry);
    dispatch({
      type: "updateAccommodationEntry",
      payload: { id: newEntry.id, updates: { tco2e, calculating: false } },
    });

    dispatch({
      type: "setCurrentAccommodationEntry",
      payload: { period: null, country: undefined, countryCode: undefined },
    });

    return { ok: true, tco2e };
  }, [state.currentAccommodationEntry]);

  const addEmployeeCommutingEntry = useCallback(async (): Promise<AddEntryResult> => {
    const distancePerDayKm = state.currentEmployeeCommutingEntry.distancePerDayKm;
    const daysPerWeek = state.currentEmployeeCommutingEntry.daysPerWeek;
    const weeksPerYear = state.currentEmployeeCommutingEntry.weeksPerYear;
    const employeeCount = state.currentEmployeeCommutingEntry.employeeCount;
    const period = state.currentEmployeeCommutingEntry.period;

    if (!state.currentEmployeeCommutingEntry.mode) {
      return { ok: false, error: "Please select a commuting mode" };
    }
    if (distancePerDayKm === undefined || distancePerDayKm === null || distancePerDayKm <= 0) {
      return { ok: false, error: "Please enter a distance per day greater than zero" };
    }
    if (daysPerWeek === undefined || daysPerWeek === null || daysPerWeek <= 0) {
      return { ok: false, error: "Please enter days per week greater than zero" };
    }
    if (weeksPerYear === undefined || weeksPerYear === null || weeksPerYear <= 0) {
      return { ok: false, error: "Please enter the number of working weeks in the reporting period" };
    }
    if (employeeCount === undefined || employeeCount === null || employeeCount <= 0) {
      return { ok: false, error: "Please enter number of employees greater than zero" };
    }
    if (!period) {
      return { ok: false, error: "Please select a reporting period" };
    }

    const passengerKm = distancePerDayKm * daysPerWeek * weeksPerYear * employeeCount;

    const newEntry: EmployeeCommutingEntry = {
      id: `commuting-${Date.now()}`,
      mode: state.currentEmployeeCommutingEntry.mode,
      distancePerDayKm,
      daysPerWeek,
      weeksPerYear,
      employeeCount,
      passengerKm,
      period,
      tco2e: null,
      calculating: true,
    };

    dispatch({ type: "addEmployeeCommutingEntry", payload: newEntry });

    const tco2e = await calculateEmployeeCommutingEmissions(newEntry);
    dispatch({
      type: "updateEmployeeCommutingEntry",
      payload: { id: newEntry.id, updates: { tco2e, calculating: false } },
    });

    dispatch({
      type: "setCurrentEmployeeCommutingEntry",
      payload: {
        mode: "car_petrol_avg",
        distancePerDayKm: 0,
        daysPerWeek: 5,
        weeksPerYear: 48,
        employeeCount: 1,
        passengerKm: 0,
        period: null,
      },
    });

    return { ok: true, tco2e };
  }, [state.currentEmployeeCommutingEntry]);

  const addSpendBasedEntry = useCallback(async (): Promise<AddEntryResult> => {
    const spendAmount = state.currentSpendBasedEntry.spendAmount;
    const period = state.currentSpendBasedEntry.period;

    if (!state.currentSpendBasedEntry.subCategory) {
      return { ok: false, error: "Please select an expense category" };
    }
    if (spendAmount === undefined || spendAmount === null || spendAmount <= 0) {
      return { ok: false, error: "Please enter a spend amount greater than zero" };
    }
    if (!period) {
      return { ok: false, error: "Please select a period" };
    }

    const newEntry: SpendBasedEntry = {
      id: `spend-${Date.now()}`,
      category: "spend_based",
      subCategory: state.currentSpendBasedEntry.subCategory,
      description: state.currentSpendBasedEntry.description,
      spendAmount,
      currency: state.currentSpendBasedEntry.currency || "USD",
      period,
      tco2e: null,
      calculating: true,
    };

    dispatch({ type: "addSpendBasedEntry", payload: newEntry });

    const tco2e = await calculateSpendBasedEmissions(newEntry);
    dispatch({
      type: "updateSpendBasedEntry",
      payload: { id: newEntry.id, updates: { tco2e, calculating: false } },
    });

    dispatch({
      type: "setCurrentSpendBasedEntry",
      payload: { category: "spend_based", subCategory: "office_supplies", currency: "USD", period: null },
    });

    return { ok: true, tco2e };
  }, [state.currentSpendBasedEntry]);

  const addFreightEntry = useCallback(async (): Promise<AddEntryResult> => {
    const period = state.currentFreightEntry.period;

    if (!state.currentFreightEntry.mode) {
      return { ok: false, error: "Please select a freight mode" };
    }
    if (!period) {
      return { ok: false, error: "Please select a period" };
    }

    let tonneKm: number | undefined;
    let parcelCount: number | undefined;
    let unit = state.currentFreightEntry.unit || "tonne_km";

    if (unit === "parcel") {
      parcelCount = state.currentFreightEntry.parcelCount;
      if (parcelCount === undefined || parcelCount === null || parcelCount <= 0) {
        return { ok: false, error: "Please enter parcel count greater than zero" };
      }
    } else {
      const weightTonnes = state.currentFreightEntry.weightTonnes;
      const distanceKm = state.currentFreightEntry.distanceKm;
      if (weightTonnes === undefined || weightTonnes === null || weightTonnes <= 0) {
        return { ok: false, error: "Please enter weight greater than zero" };
      }
      if (distanceKm === undefined || distanceKm === null || distanceKm <= 0) {
        return { ok: false, error: "Please enter distance greater than zero" };
      }
      tonneKm = weightTonnes * distanceKm;
    }

    const newEntry: FreightEntry = {
      id: `freight-${Date.now()}`,
      direction: state.currentFreightEntry.direction || "upstream",
      mode: state.currentFreightEntry.mode,
      weightTonnes: state.currentFreightEntry.weightTonnes,
      distanceKm: state.currentFreightEntry.distanceKm,
      parcelCount,
      tonneKm,
      unit,
      period,
      tco2e: null,
      calculating: true,
    };

    dispatch({ type: "addFreightEntry", payload: newEntry });

    const tco2e = await calculateFreightEmissions(newEntry);
    dispatch({
      type: "updateFreightEntry",
      payload: { id: newEntry.id, updates: { tco2e, calculating: false } },
    });

    dispatch({
      type: "setCurrentFreightEntry",
      payload: { direction: newEntry.direction, mode: "road_freight_hgv", unit: "tonne_km", period: null },
    });

    return { ok: true, tco2e };
  }, [state.currentFreightEntry]);

  const actions = useMemo<Scope3Actions>(
    () => ({
      setScope3Data: (value) => dispatch({ type: "setScope3Data", payload: value }),
      updateScope3Data: (updates) => dispatch({ type: "updateScope3Data", payload: updates }),
      updateScope3Coverage: (updates) => dispatch({ type: "updateScope3Coverage", payload: updates }),
      setCurrentBusinessTravelEntry: (value) => dispatch({ type: "setCurrentBusinessTravelEntry", payload: value }),
      setCurrentAccommodationEntry: (value) => dispatch({ type: "setCurrentAccommodationEntry", payload: value }),
      setCurrentEmployeeCommutingEntry: (value) =>
        dispatch({ type: "setCurrentEmployeeCommutingEntry", payload: value }),
      setCurrentSpendBasedEntry: (value) => dispatch({ type: "setCurrentSpendBasedEntry", payload: value }),
      setCurrentFreightEntry: (value) => dispatch({ type: "setCurrentFreightEntry", payload: value }),
      addBusinessTravelEntry,
      addAccommodationEntry,
      addEmployeeCommutingEntry,
      addSpendBasedEntry,
      addFreightEntry,
      updateBusinessTravelEntry: (id, updates) => dispatch({ type: "updateBusinessTravelEntry", payload: { id, updates } }),
      updateEmployeeCommutingEntry: (id, updates) => dispatch({ type: "updateEmployeeCommutingEntry", payload: { id, updates } }),
      updateFreightEntry: (id, updates) => dispatch({ type: "updateFreightEntry", payload: { id, updates } }),
      updateSpendBasedEntry: (id, updates) => dispatch({ type: "updateSpendBasedEntry", payload: { id, updates } }),
      deleteBusinessTravelEntry: (id) => dispatch({ type: "deleteBusinessTravelEntry", payload: id }),
      deleteAccommodationEntry: (id) => dispatch({ type: "deleteAccommodationEntry", payload: id }),
      deleteEmployeeCommutingEntry: (id) => dispatch({ type: "deleteEmployeeCommutingEntry", payload: id }),
      deleteSpendBasedEntry: (id) => dispatch({ type: "deleteSpendBasedEntry", payload: id }),
      deleteFreightEntry: (id) => dispatch({ type: "deleteFreightEntry", payload: id }),
    }),
    [
      addAccommodationEntry,
      addBusinessTravelEntry,
      addEmployeeCommutingEntry,
      addFreightEntry,
      addSpendBasedEntry,
    ]
  );

  return { state, actions };
}
